import React, { useState, useEffect, useContext } from 'react';
import { hasRole, PERMISSIONS } from '../../utils/routeAuth';
import { useSelector, useDispatch } from 'react-redux';
import { Container } from "styled-bootstrap-grid";
import { getAllVersions, createVersion, updateVersion, deleteVersion } from '../../redux/actions/memberAction';
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Box,
  Button,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Edit as EditIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import colors from '../../shared/constants/color';
import { PageLayout } from "../../components";
import { ThemeContext } from "../../context/ThemeContextProvider";
import { getBreadcrumbLinkSx, getWiserTheme } from "../../shared/theme/wiserThemeHelpers";
import { useToast } from "../../shared/hooks/useToast";


interface VersionData {
  id?: string;
  version: string;
  title: string;
  description: string;
  features: string[];
  isCurrent?: boolean;
}

const Version: React.FC = () => {
  const dispatch = useDispatch();
  const { darkModeActive } = useContext(ThemeContext);
  const { showToast } = useToast();
  const versions = useSelector((state: any) => state.versions?.versions || []) as VersionData[];

  const [localVersions, setLocalVersions] = useState<VersionData[]>([]);
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const [editingFeatures, setEditingFeatures] = useState<string[]>([]);
  const [editingTitle, setEditingTitle] = useState<string>('');
  const [editingDescription, setEditingDescription] = useState<string>('');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [versionToDelete, setVersionToDelete] = useState<VersionData | null>(null);
  const [newVersion, setNewVersion] = useState<Partial<VersionData>>({
    version: '',
    title: '',
    description: '',
    features: [''],
    isCurrent: false,
  });
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [newVersionErrors, setNewVersionErrors] = useState<{
    version?: string;
    title?: string;
    description?: string;
  }>({});

  useEffect(() => {
    try {
      setCanEdit(hasRole(localStorage.getItem('roles'), PERMISSIONS.VERSION_ADMIN));
    } catch {
      setCanEdit(false);
    }
  }, []);

  useEffect(() => {
    dispatch(getAllVersions() as any);
  }, [dispatch]);

  useEffect(() => {
    if (versions && versions.length > 0) {
      setLocalVersions(versions);
    }
  }, [versions]);

  const saveVersion = async (versionData: VersionData) => {
    try {
      if (versionData.id) {
        await dispatch(updateVersion({
          VersionId: parseInt(versionData.id),
          Version: versionData.version,
          Title: versionData.title,
          Description: versionData.description,
          Features: versionData.features,
          IsCurrent: versionData.isCurrent || false,
        }) as any);
        showToast("Sürüm başarıyla güncellendi.", "success");
      } else {
        await dispatch(createVersion({
          Version: versionData.version,
          Title: versionData.title,
          Description: versionData.description,
          Features: versionData.features,
          IsCurrent: versionData.isCurrent || false,
        }) as any);
        showToast("Sürüm başarıyla eklendi.", "success");
      }

      setEditingVersionId(null);
      setOpenAddDialog(false);
    } catch (error: any) {
      console.error('Versiyon kaydedilemedi:', error);
      showToast("İşlem gerçekleşirken bir hata oldu.", "error");
    }
  };

  const handleDeleteVersion = async (versionId: string) => {
    try {
      await dispatch(deleteVersion(parseInt(versionId)) as any);
      setOpenDeleteDialog(false);
      setVersionToDelete(null);
      showToast("Sürüm başarıyla silindi.", "success");
    } catch (error: any) {
      console.error('Versiyon silinemedi:', error);
      showToast("İşlem gerçekleşirken bir hata oldu.", "error");
    }
  };

  const handleEdit = (version: VersionData) => {
    setEditingVersionId(version.id || null);
    setEditingFeatures([...version.features]);
    setEditingTitle(version.title);
    setEditingDescription(version.description);
  };

  const handleSaveEdit = () => {
    const versionToUpdate = localVersions.find(v => v.id === editingVersionId);
    if (versionToUpdate) {
      const updatedVersion: VersionData = {
        ...versionToUpdate,
        title: editingTitle,
        description: editingDescription,
        features: editingFeatures.filter(f => f.trim() !== ''),
      };
      saveVersion(updatedVersion);
    }
  };

  const handleCancelEdit = () => {
    setEditingVersionId(null);
    setEditingFeatures([]);
    setEditingTitle('');
    setEditingDescription('');
  };

  const handleAddFeature = (versionId: string) => {
    setEditingFeatures([...editingFeatures, '']);
  };

  const handleRemoveFeature = (index: number) => {
    setEditingFeatures(editingFeatures.filter((_, i) => i !== index));
  };

  const handleFeatureChange = (index: number, value: string) => {
    const updated = [...editingFeatures];
    updated[index] = value;
    setEditingFeatures(updated);
  };

  const handleAddVersion = () => {
    setNewVersion({
      version: '',
      title: '',
      description: '',
      features: [''],
      isCurrent: false,
    });
    setNewVersionErrors({});
    setOpenAddDialog(true);
  };

  const handleSaveNewVersion = () => {
    const errors: { version?: string; title?: string; description?: string } = {};

    if (!newVersion.version) {
      errors.version = 'Sürüm numarası zorunludur.';
    }
    if (!newVersion.title) {
      errors.title = 'Başlık zorunludur.';
    }
    if (!newVersion.description) {
      errors.description = 'Açıklama zorunludur.';
    }

    setNewVersionErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const versionData: VersionData = {
      version: newVersion.version!,
      title: newVersion.title!,
      description: newVersion.description!,
      features: newVersion.features?.filter(f => f.trim() !== '') || [],
      isCurrent: newVersion.isCurrent || false,
    };

    saveVersion(versionData);
    setNewVersion({
      version: '',
      title: '',
      description: '',
      features: [''],
      isCurrent: false,
    });
    setNewVersionErrors({});
  };

  const handleDeleteClick = (version: VersionData) => {
    setVersionToDelete(version);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (versionToDelete?.id) {
      handleDeleteVersion(versionToDelete.id);
    }
  };

  return (
     <PageLayout title="Sürüm Notları" darkModeActive={darkModeActive}>
        <Container fluid style={{ padding: '32px 120px 32px 50px' }}>
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link underline="hover" color="inherit" href="/" sx={getBreadcrumbLinkSx(darkModeActive)}>Anasayfa</Link>
            <Typography sx={getBreadcrumbLinkSx(darkModeActive)}>Sürüm Notları</Typography>
          </Breadcrumbs>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
          {canEdit && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddVersion}
              sx={{ ml: 2 }}
            >
              Sürüm Ekle
            </Button>
          )}
        </Box>

        {localVersions.map((version, versionIndex) => {
          const isEditing = editingVersionId === version.id;
          const isCurrent = version.isCurrent;

          return (
            <React.Fragment key={version.id}>
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    {isEditing ? (
                      <TextField
                        fullWidth
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    ) : (
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 500,
                          color: darkModeActive
                            ? (isCurrent ? getWiserTheme(true).accent : getWiserTheme(true).textPrimary)
                            : (isCurrent ? colors.blue.B700 : colors.gray1100),
                        }}
                      >
                        {`${version.version} ${version.title}`}
                      </Typography>
                    )}
                    {isEditing ? (
                      <TextField
                        fullWidth
                        value={editingDescription}
                        onChange={(e) => setEditingDescription(e.target.value)}
                        variant="outlined"
                        size="small"
                        multiline
                        rows={2}
                        sx={{ mt: 1 }}
                      />
                    ) : (
                      <Typography
                        variant="subtitle1"
                        gutterBottom
                        sx={{
                          mt: 1,
                          mb: 2,
                          color: darkModeActive ? getWiserTheme(true).textSecondary : undefined,
                        }}
                      >
                        {version.description}
                      </Typography>
                    )}
                  </Box>
                  {canEdit && (
                    <Box sx={{ ml: 2, display: 'flex', gap: 1 }}>
                      {isEditing ? (
                        <>
                          <Tooltip title="Kaydet">
                            <IconButton
                              color="primary"
                              onClick={handleSaveEdit}
                              size="small"
                            >
                              <SaveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="İptal">
                            <IconButton
                              color="secondary"
                              onClick={handleCancelEdit}
                              size="small"
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <>
                          <Tooltip title="Düzenle">
                            <IconButton
                              color="primary"
                              onClick={() => handleEdit(version)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sil">
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteClick(version)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  )}
                </Box>

                {isEditing ? (
                  <List>
                    {editingFeatures.map((feature, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                        <TextField
                          fullWidth
                          value={feature}
                          onChange={(e) => handleFeatureChange(idx, e.target.value)}
                          variant="outlined"
                          size="small"
                          multiline
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              fontSize: '14px',
                            },
                          }}
                        />
                        <IconButton
                          onClick={() => handleRemoveFeature(idx)}
                          size="small"
                          sx={{ ml: 1 }}
                          color="error"
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </ListItem>
                    ))}
                    <ListItem sx={{ py: 0.5, px: 0 }}>
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => handleAddFeature(version.id!)}
                        size="small"
                        variant="outlined"
                      >
                        Özellik Ekle
                      </Button>
                    </ListItem>
                  </List>
                ) : (
                  <List>
                    {version.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            fontSize: '14px',
                            color: darkModeActive
                              ? getWiserTheme(true).textPrimary
                              : (isCurrent ? colors.gray1200 : colors.gray1300),
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>

              {versionIndex < localVersions.length - 1 && (
                <Divider
                  sx={{
                    my: 3,
                    borderColor: darkModeActive ? getWiserTheme(true).border : undefined,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* Silme Onay Dialog */}
        <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
          <DialogTitle>Sürümü Sil</DialogTitle>
          <DialogContent>
            <Typography>
              <strong>{versionToDelete?.version} {versionToDelete?.title} </strong> sürümünü silmek istediğinizden emin misiniz?
              <br />
              <br />
              Bu işlem geri alınamaz.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDeleteDialog(false)}>İptal</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              SİL
            </Button>
          </DialogActions>
        </Dialog>

        {/* Yeni Sürüm Ekleme Dialog */}
        <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Yeni Sürüm Ekle</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="Sürüm Numarası"
                value={newVersion.version}
                onChange={(e) => setNewVersion({ ...newVersion, version: e.target.value })}
                placeholder="Örn: 1.4"
                fullWidth
                required
                error={!!newVersionErrors.version}
                helperText={newVersionErrors.version}
              />
              <TextField
                label="Başlık"
                value={newVersion.title}
                onChange={(e) => setNewVersion({ ...newVersion, title: e.target.value })}
                placeholder="Örn: v.1.4 - Yeni Sürüm"
                fullWidth
                required
                error={!!newVersionErrors.title}
                helperText={newVersionErrors.title}
              />
              <TextField
                label="Açıklama"
                value={newVersion.description}
                onChange={(e) => setNewVersion({ ...newVersion, description: e.target.value })}
                placeholder="Bu sürümde yapılan değişiklikler:"
                fullWidth
                multiline
                rows={2}
                required
                error={!!newVersionErrors.description}
                helperText={newVersionErrors.description}
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Özellikler
                </Typography>
                {(newVersion.features || []).map((feature, idx) => (
                  <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      fullWidth
                      value={feature}
                      onChange={(e) => {
                        const updated = [...(newVersion.features || [])];
                        updated[idx] = e.target.value;
                        setNewVersion({ ...newVersion, features: updated });
                      }}
                      variant="outlined"
                      size="small"
                      placeholder="Özellik açıklaması"
                    />
                    <IconButton
                      onClick={() => {
                        const updated = (newVersion.features || []).filter((_, i) => i !== idx);
                        setNewVersion({ ...newVersion, features: updated });
                      }}
                      size="small"
                      color="error"
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setNewVersion({
                      ...newVersion,
                      features: [...(newVersion.features || []), ''],
                    });
                  }}
                  size="small"
                  variant="outlined"
                >
                  Özellik Ekle
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAddDialog(false)}>İptal</Button>
            <Button onClick={handleSaveNewVersion} variant="contained">
              Kaydet
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </PageLayout>
  );
};

export default Version; 